/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import classNames from "classnames";

import Button, {
  BackButton,
  FilledButton,
  OutlinedButton,
  TextButton,
} from "components/common/Button/Button";
import ButtonLink from "components/common/Links/ButtonLink/ButtonLink";
import StyledLink from "components/common/Links/StyledLink/StyledLink";
import {
  ABOUT_US,
  PROFILE_SUMMARY_TAB,
  PROFILE_SETTINGS,
  CHANGE_PASSWORD,
  USER_ZONE,
  MASSAGE_MASTER_ZONE_BENEFITS,
  MASSAGE_MASTER_ZONE_PRICE,
  POST_NEW_ANNOUNCEMENT,
  PROVIDED_MASSAGES,
  SERVICE_TERMS,
  SIGN_IN,
  SIGN_UP,
  EDUCATION,
  SUPPLY,
  CHANGE_EMAIL,
  SEARCH_MASSAGE,
  MASSAGE_MASTER_ZONE,
} from "constants/routes";
import classes from "./NavLinks.module.css";
import Select from "components/common/Inputs/Select/Select";
import MobilePageHeading from "../MobilePageHeading/MobilePageHeading";
import ArrowLink from "components/common/Links/ArrowLink/ArrowLink";
import PlacesAutocomplete from "components/common/Inputs/PlacesAutocomplete/PlacesAutocomplete";
import {
  massageMainTypeOptions,
  massageTypeFilterGroupOptions,
} from "constants/massageTypes";
import { Option } from "utils/interfaces/commonInterfacesAndTypes";
import FitlerGroup from "components/common/Inputs/FilterGroup/FilterGroup";
import ArrowIcon from "static/icons/arrow_without_tail.svg";
import useTranslation from "next-translate/useTranslation";
import { Place } from "utils/interfaces/announcementForm";
import { useAuth } from "../../../../../context/auth/AuthContextProvider";

type NavLinksProps = {
  expanded?: boolean;
};
type HomeNavLinksProps = {
  handleSearchMassage: () => void;
  handleCategorySelect: (value: Option) => void;
  selectedCategory: Option | null;
  handlePlaceChange: (value: Place) => void;
  handleClearPlace: () => void;
  place: Place | null;
};

export const HomeNavLinks = ({
  expanded,
  handleSearchMassage,
  handleCategorySelect,
  selectedCategory,
  handlePlaceChange,
  handleClearPlace,
  place,
}: NavLinksProps & HomeNavLinksProps) => {
  const { t } = useTranslation("common");
  const { user } = useAuth();

  const isLoggedIn = !!user?._id;

  return (
    <div
      className={classNames(classes.navLinks, { [classes.expanded]: expanded })}
    >
      <ul>
        <li
          className={classNames(classes.mobileOnly, classes.massageTypeSelect)}
        >
          <Select
            selectedOption={selectedCategory}
            placeholder={t("select_massage_type_placeholder")}
            options={massageMainTypeOptions}
            onChangeHandler={handleCategorySelect}
          />
        </li>
        <li className={classes.mobileOnly}>
          <PlacesAutocomplete
            onChange={handlePlaceChange}
            value={place}
            onClear={handleClearPlace}
          />
        </li>
        <li className={classes.navItem}>
          <Link href={MASSAGE_MASTER_ZONE}>
            <a>{t("know_us")}</a>
          </Link>
        </li>
        <li className={classes.navItem}>
          <Link href={ABOUT_US}>
            <a>{t("about_us")}</a>
          </Link>
        </li>
        {isLoggedIn ? (
          <li className={classes.navItem}>
            <Link href={SEARCH_MASSAGE}>
              <a>{t("search_for_masseur")}</a>
            </Link>
          </li>
        ) : null}
      </ul>

      {isLoggedIn ? (
        <ButtonLink
          href={PROFILE_SUMMARY_TAB}
          className={classes.filledButtonLink}
        >
          {t("user_zone")}
        </ButtonLink>
      ) : (
        <div className={classes.buttonsSection}>
          <StyledLink
            href={SIGN_IN}
            text={t("log_in")}
            className={classes.outlineButtonLink}
          />
          <ButtonLink href={SIGN_UP} className={classes.filledButtonLink}>
            {t("sign_up")}
          </ButtonLink>
        </div>
      )}
    </div>
  );
};

type MassageMasterZoneNavLinksProps = NavLinksProps & {
  visibleReturnMainPageBtn?: boolean;
};

export const MassageMasterZoneNavLinks = ({
  visibleReturnMainPageBtn,
  expanded,
}: MassageMasterZoneNavLinksProps) => {
  const { t } = useTranslation("common");
  const { user } = useAuth();

  const isLoggedIn = !!user?._id;

  return (
    <div
      className={classNames(classes.navLinks, { [classes.expanded]: expanded })}
    >
      <ul>
        <li className={classes.navItem}>
          <Link href={MASSAGE_MASTER_ZONE_BENEFITS}>
            <a>{t("benefits")}</a>
          </Link>
        </li>
        <li className={classes.navItem}>
          <Link href={MASSAGE_MASTER_ZONE_PRICE}>
            <a>{t("pricing")}</a>
          </Link>
        </li>
        <li className={classes.navItem}>
          <Link href={EDUCATION}>
            <a>{t("education")}</a>
          </Link>
        </li>
        <li className={classes.navItem}>
          <Link href={SUPPLY}>
            <a>{t("supplies")}</a>
          </Link>
        </li>
      </ul>

      {isLoggedIn ? (
        <ButtonLink
          href={PROFILE_SUMMARY_TAB}
          className={classes.filledButtonLink}
        >
          {t("user_zone")}
        </ButtonLink>
      ) : (
        <div className={classes.buttonsSection}>
          <StyledLink
            href={SIGN_IN}
            text={t("log_in")}
            className={classes.outlineButtonLink}
          />
          <ButtonLink href={SIGN_UP} className={classes.filledButtonLink}>
            {t("sign_up")}
          </ButtonLink>
        </div>
      )}
    </div>
  );
};

type LoggedInNavLinks = NavLinksProps & {
  onLogoutHandler: () => void;
  openRemoveAccountModalHandler: () => void;
};

export const MassageMasterLoggedInNavLinks = ({
  expanded,
  onLogoutHandler,
  openRemoveAccountModalHandler,
}: LoggedInNavLinks) => {
  const [mobileSettingsMenuOpened, setMobileSettingsMenuOpened] =
    useState(false);

  const onOpenMobileSettingsMenuHandler = () =>
    setMobileSettingsMenuOpened(true);
  const onCloseMobileSettigsMenuHandler = () =>
    setMobileSettingsMenuOpened(false);
  const { t } = useTranslation("common");

  const mobileSettingsMenu = (
    <div
      className={classNames(classes.mobileSettingsContainer, {
        [classes.visible]: mobileSettingsMenuOpened,
      })}
    >
      <nav className={classes.mobileNavContent}>
        <BackButton
          onClick={onCloseMobileSettigsMenuHandler}
          className={classes.backBtnMobileSettingsMenu}
        />
        <MobilePageHeading text={t("settings")} />
        <div className={classes.navLinks}>
          <ul>
            {/*<li>*/}
            {/*  <ArrowLink href={CHANGE_EMAIL} text={t("change_email")} />*/}
            {/*</li>*/}
            {/*<li>*/}
            {/*  <ArrowLink href={CHANGE_PASSWORD} text={t("change_password")} />*/}
            {/*</li>*/}
            <li>
              <ArrowLink
                href={SERVICE_TERMS}
                text={t("terms_of_use_and_privacy_policy")}
              />
            </li>
            {/*<li>*/}
            {/*  <TextButton*/}
            {/*    type="button"*/}
            {/*    onClick={openRemoveAccountModalHandler}*/}
            {/*    content={*/}
            {/*      <div className={classes.removeAccountMobileBtnContent}>*/}
            {/*        <span>{t("delete_account")}</span>*/}
            {/*        <Image src={ArrowIcon} width="24" height="24" alt="" />*/}
            {/*      </div>*/}
            {/*    }*/}
            {/*    className={classes.removeAccountMobileBtn}*/}
            {/*  />*/}
            {/*</li>*/}
          </ul>
        </div>
      </nav>
    </div>
  );

  return (
    <div
      className={classNames(classes.navLinks, { [classes.expanded]: expanded })}
    >
      <ul>
        <li
          className={classNames(classes.activeAnnouncements, classes.navItem)}
        >
          <Link href={PROFILE_SUMMARY_TAB}>
            <a>{t("profile_settings.profile")}</a>
          </Link>
        </li>
        <li
          className={classNames(
            classes.announcementsMobileMenu,
            classes.navItem
          )}
        >
          <Link href={PROFILE_SETTINGS}>
            <a>{t("profile_settings.profile")}</a>
          </Link>
        </li>
        {/*<li*/}
        {/*  className={classNames(classes.addAnnouncementLink, classes.navItem)}*/}
        {/*>*/}
        {/*  <Link href={POST_NEW_ANNOUNCEMENT}>*/}
        {/*    <a>{t("add_advertisement")}</a>*/}
        {/*  </Link>*/}
        {/*</li>*/}
        {/*<li className={classes.navItem}>*/}
        {/*  <Link href={PAYMENT}>*/}
        {/*    <a>{t("payments")}</a>*/}
        {/*  </Link>*/}
        {/*</li>*/}
        <li className={classes.desktopSettingsLinkContainer}>
          <div className={classes.dropdown}>
            <a className={classes.dropdownLink}>{t("settings")}</a>
            <div className={classes.dropdownContainer}>
              <div className={classes.dropdownContent}>
                {/*<Link href={CHANGE_EMAIL}>*/}
                {/*  <a>{t("change_email")}</a>*/}
                {/*</Link>*/}
                {/*<Link href={CHANGE_PASSWORD}>*/}
                {/*  <a>{t("change_password")}</a>*/}
                {/*</Link>*/}
                <Link href={SERVICE_TERMS}>
                  <a>{t("terms_of_use_and_privacy_policy")}</a>
                </Link>
                {/*<TextButton*/}
                {/*  type="button"*/}
                {/*  onClick={openRemoveAccountModalHandler}*/}
                {/*  content={t("delete_account")}*/}
                {/*/>*/}
              </div>
            </div>
          </div>
        </li>
        <li className={classes.mobileSettingsBtnContainer}>
          <Button
            content={t("settings")}
            type="button"
            onClick={onOpenMobileSettingsMenuHandler}
            className={classNames(classes.mobileSettingsBtn, classes.navItem)}
          />
          {mobileSettingsMenuOpened && mobileSettingsMenu}
        </li>
      </ul>
      <div className={classes.buttonsSection}>
        <OutlinedButton
          type="button"
          onClick={onLogoutHandler}
          content={t("log_out")}
          className={classes.logoutButton}
        />
        {/*<ButtonLink*/}
        {/*  href={POST_NEW_ANNOUNCEMENT}*/}
        {/*  className={classNames(*/}
        {/*    classes.filledButtonLink,*/}
        {/*    classes.addAnnouncementBtn*/}
        {/*  )}*/}
        {/*>*/}
        {/*  {t("add_advertisement")}*/}
        {/*</ButtonLink>*/}
      </div>
    </div>
  );
};

type SearchMassageNavLinksProps = NavLinksProps & {
  onChangeCategoryMainFilterHandler: (category: string) => void;
  onChangeCategorySubFilterHandler: (
    mainCategory: string,
    subCategory: string
  ) => void;
  onApplyCategoriesFilterHandler: () => void;
  selectedCategories: {
    [key: string]: string[] | { [key: string]: string[] };
  };
  handlePlaceChange: (place: Place) => void;
  place: Place | null;
  handleSearchMassageByPlace: () => void;
  onClearMainCategoriesHandler: () => void;
  onClearSubCategoriesHandler: (mainCategory: string) => void;
  handleSearchMassageByPlaceAndCategory: () => void;
  handleCategorySelect: (value: Option) => void;
  selectedCategory: Option | null;
  handleClearPlace: () => void;
};
export const SearchMassageNavLinks = ({
  onChangeCategoryMainFilterHandler,
  onChangeCategorySubFilterHandler,
  selectedCategories,
  handlePlaceChange,
  place,
  expanded,
  onApplyCategoriesFilterHandler,
  handleSearchMassageByPlace,
  onClearMainCategoriesHandler,
  onClearSubCategoriesHandler,
  handleSearchMassageByPlaceAndCategory,
  handleCategorySelect,
  selectedCategory,
  handleClearPlace,
}: SearchMassageNavLinksProps) => {
  const { t } = useTranslation("common");

  return (
    <>
      <div
        className={classNames(classes.filters, classes.desktopView, {
          [classes.expanded]: expanded,
        })}
      >
        <ul>
          <li>
            <FitlerGroup
              options={massageTypeFilterGroupOptions}
              selectedMainOptions={
                selectedCategories.mainCategories as string[]
              }
              selectedSubOptions={
                selectedCategories.subCategories as { [key: string]: string[] }
              }
              onChangeMainOptionHandler={onChangeCategoryMainFilterHandler}
              onChangeSubOptionHandler={onChangeCategorySubFilterHandler}
              onClearMainOptionsHandler={onClearMainCategoriesHandler}
              onClearSubOptionsHandler={onClearSubCategoriesHandler}
              onSubmitHandler={onApplyCategoriesFilterHandler}
              placeholder={t("type_of_treatment")}
            />
          </li>
          <li>
            <PlacesAutocomplete
              onChange={handlePlaceChange}
              value={place}
              onClear={handleClearPlace}
            />
          </li>
        </ul>
        <FilledButton
          type="button"
          onClick={handleSearchMassageByPlace}
          content={t("search")}
          className={classes.searchButton}
        />
      </div>
      <div
        className={classNames(classes.navLinks, classes.mobileView, {
          [classes.expanded]: expanded,
        })}
      >
        <ul>
          <li
            className={classNames(
              classes.mobileOnly,
              classes.massageTypeSelect
            )}
          >
            <Select
              selectedOption={selectedCategory}
              placeholder={t("select_massage_type_placeholder")}
              options={massageMainTypeOptions}
              onChangeHandler={handleCategorySelect}
            />
          </li>
          <li className={classes.mobileOnly}>
            <PlacesAutocomplete
              onChange={handlePlaceChange}
              value={place}
              onClear={handleClearPlace}
            />
          </li>
          <li className={classes.navItem}>
            <Link href={MASSAGE_MASTER_ZONE}>
              <a>{t("know_us")}</a>
            </Link>
          </li>
          <li className={classes.navItem}>
            <Link href={ABOUT_US}>
              <a>{t("about_us")}</a>
            </Link>
          </li>
        </ul>
        <FilledButton
          type="button"
          onClick={handleSearchMassageByPlaceAndCategory}
          content={t("search_for_masseur")}
          className={classNames(classes.searchButton, classes.searchMassageBtn)}
        />
      </div>
    </>
  );
};
